import { createContext, useContext, useState, useEffect } from 'react';
import { supabase, auth, syncSession } from '../lib/supabase';
import { requireVerifiedSession, VALET_PROFILE_COLUMNS } from '../lib/sessionGuard';
import { handleAuthCallbackFromUrl } from '@/utils/authCallback';
import { createScopedLogger } from '@/utils/logger.js';
import { safeGetItem, safeRemoveItem } from '@/utils/safeLocalStorage';
import { COUNCIL_BADGES, ADMIN_BADGES, ARCHITECT_BADGES } from '@/constants/authorityBadges';

const AuthContext = createContext();
const log = createScopedLogger('AuthContext');

// Badge hierarchy for role determination (highest to lowest)
// architect = owner, high-council = super admin, council = admin, kot = manager, captain = supervisor
/**
 * badge -> role ladder, FIRST match wins, so order is the hierarchy.
 *
 * Dropped 2026-08-21: 'platform-owner' and 'high-council' — neither has a badge
 * definition or a single holder, so they only ever looked like authority.
 * Added: 'council-member' beside 'council' (see constants/authorityBadges.js —
 * the badge NAMED "Council Member" is actually badge_type 'admin-access', which
 * is how these two drifted apart in the first place).
 *
 * `founder` is deliberately NOT here. It is not an oversight: all 5 holders also
 * hold council or kot, so they already resolve to a role, and inserting founder
 * at the top would move them from role 'admin' to role 'owner' — which would
 * flip `isAdmin` (userRole === 'admin') to FALSE for the founders. Latent, not
 * live; changing it needs its own pass over every userRole consumer.
 */
const ROLE_BADGE_HIERARCHY = [
  { badge: 'architect', role: 'owner' },
  { badge: 'super-admin', role: 'super-admin' },
  { badge: 'council', role: 'admin' },
  { badge: 'council-member', role: 'admin' },
  { badge: 'admin-access', role: 'admin' },
  { badge: 'kot', role: 'manager' },
  { badge: 'kot-manager', role: 'manager' },
  { badge: 'captain', role: 'supervisor' },
  { badge: 'coordinator', role: 'supervisor' },
];

// Badges that grant dashboard access (non-valet routes). The authority tiers are
// composed from the ONE definition so this list cannot drift from the gates again;
// the rest are operational roles that legitimately reach the dashboard.
const DASHBOARD_ACCESS_BADGES = [
  ...COUNCIL_BADGES,
  ...ADMIN_BADGES,
  ...ARCHITECT_BADGES,
  'kot', 'kot-manager', 'captain', 'coordinator', 'manager',
];

const deriveRoleFromBadges = (badges = [], fallback = 'valet') => {
  if (!Array.isArray(badges) || badges.length === 0) {
    return fallback;
  }

  for (const { badge, role } of ROLE_BADGE_HIERARCHY) {
    if (badges.includes(badge)) {
      return role;
    }
  }

  return fallback;
};

/**
 * Sync OAuth provider data to valet profile after login.
 * - Discord: writes discord_user_id + discord_username (if not already set)
 * - Google/Apple: writes avatar_url (if valet has none — real photos, safe to auto-set)
 * - Discord avatars are gaming/anime — skip auto-set (too risky)
 * - NEVER touches first_name/last_name
 */
const syncOAuthDataToProfile = async (user, valetDoc) => {
  if (!user?.identities || !valetDoc?.id) return;

  const updates = {};

  // 1) Discord ID sync — only if not already set
  const discordIdentity = user.identities.find(i => i.provider === 'discord');
  if (discordIdentity && !valetDoc.discord_user_id) {
    const idData = discordIdentity.identity_data || {};
    const discordId = idData.sub || idData.id;
    const discordUsername = idData.preferred_username || idData.username;
    if (discordId) {
      updates.discord_user_id = discordId;
      if (discordUsername) updates.discord_username = discordUsername;
      log.debug?.('Syncing Discord ID to valet profile', { discordId, discordUsername });
    }
  }

  // 2) Avatar capture — only for non-Discord providers, only if valet has no avatar
  if (!valetDoc.avatar_url) {
    const provider = user.app_metadata?.provider;
    const avatarUrl = user.user_metadata?.avatar_url || user.user_metadata?.picture;
    if (avatarUrl && provider !== 'discord') {
      updates.avatar_url = avatarUrl;
      log.debug?.('Capturing OAuth avatar for valet profile', { provider });
    }
  }

  // 3) Write updates if any
  if (Object.keys(updates).length > 0) {
    const { error } = await supabase
      .from('valets')
      .update(updates)
      .eq('id', valetDoc.id);

    if (error) {
      log.error?.('Failed to sync OAuth data to valet profile', error);
    } else {
      log.debug?.('OAuth data synced to valet profile', updates);
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [valetProfile, setValetProfile] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userBadges, setUserBadges] = useState([]);
  // Location-scoped leadership (captain/kot/manager on an ACTIVE location_roster) grants
  // dashboard access even when the user has no global leadership BADGE. Codex's 2026-07-08
  // scope fix moved captains off global captain badges onto location_rosters — the badge-only
  // gate stopped seeing them and bounced them to /valet. Canonical reader: is_kot_leader().
  const [isRosterLeader, setIsRosterLeader] = useState(false);
  // Start loading=true so App.jsx shows spinner until bootstrapAuth completes session check.
  // Without this, there's a race: user=null + loading=false + authReady=true = immediate redirect to login
  // before getSession() has a chance to find the existing session. Emergency timeout (3s) prevents infinite spinner.
  const [loading, setLoading] = useState(true);
  // authReady = true by default - only set false when actively processing OAuth code exchange
  const [authReady, setAuthReady] = useState(true);
  const [rolesReady, setRolesReady] = useState(false);
  // Explicit, NON-silent failure signal. When role resolution times out / errors we still
  // provisionally default to 'valet' so the app renders, but that empty-badge state is
  // indistinguishable from a genuine valet unless we flag it. rolesDegraded=true tells
  // consumers "this is a degraded read, not ground truth" — so guards can offer a retry
  // instead of treating a founder as badge-less during an incident. Cleared on success.
  const [rolesDegraded, setRolesDegraded] = useState(false);

  useEffect(() => {
    // EMERGENCY TIMEOUT: Force auth to complete after 3 seconds max
    // Uses ref to avoid firing when auth already completed normally
    let authCompleted = false;
    let authEffectActive = true;
    const emergencyTimeout = setTimeout(() => {
      if (!authCompleted) {
        console.warn('[AuthContext] EMERGENCY: Forcing auth ready after timeout');
        setAuthReady(true);
        setLoading(false);
      }
    }, 3000);

    const bootstrapAuth = async () => {
      log.debug?.('AuthContext: bootstrapAuth starting');

      // Skip auth callback handling on password reset page - let it handle its own auth
      const isResetPasswordPage = window.location.pathname === '/auth/reset-password';
      if (isResetPasswordPage) {
        log.debug?.('On reset-password page, skipping auth callback handling');
        setLoading(false);
        authCompleted = true;
        return;
      }

      // Check if there's an OAuth code in URL
      // IMPORTANT: detectSessionInUrl is enabled in supabase-config when code= is present
      // This means Supabase will AUTOMATICALLY exchange the code via its internal init
      // We must NOT call exchangeCodeForSession manually - that causes a race/deadlock!
      // Instead, just wait for onAuthStateChange to fire with the session
      const hasOAuthCode = window.location.search.includes('code=');
      if (hasOAuthCode) {
        console.log('[AuthContext] OAuth code in URL - waiting for Supabase to exchange via detectSessionInUrl');
        // CRITICAL: Set authReady to false so downstream components know we're still processing
        setAuthReady(false);
        setLoading(true);
        // DO NOT clear the code from URL here! Supabase needs it to exchange for a session.
        // The code will be cleared in onAuthStateChange after successful exchange.
        // Don't set loading=false - let onAuthStateChange handle it when exchange completes
        // onAuthStateChange will fire with SIGNED_IN event after successful exchange
        return;
      }

      // No OAuth code - auth client is ready immediately
      setAuthReady(true);

      try {
        const callbackResult = await handleAuthCallbackFromUrl();

        if (callbackResult?.session) {
          const callbackType = callbackResult.callbackType || 'unknown';
          log.debug?.('Auth callback detected', { callbackType });

          const authUser = callbackResult.session.user ?? null;

          if (callbackType === 'signup' && authUser) {
            log.debug?.('Email confirmed for signup flow');
          }

          setUser(authUser);

          if (authUser) {
            try {
              await syncSession(callbackResult.session);
              log.debug?.('Session synced to data client', { callbackType });
            } catch (syncError) {
              log.error?.(`Failed to sync session after ${callbackType}`, syncError);
            }
          }

          setLoading(false);
          authCompleted = true;
          return;
        }
      } catch (_error) {
        log.error?.('Failed to handle auth callback', _error);
        // Continue to check existing session even if callback handling failed
      }

      log.debug?.('No auth callback detected, checking existing session');

      // Detect deeply stale sessions BEFORE getSession() triggers auto-refresh.
      // If the stored session expired >1hr ago, the refresh token is likely dead
      // and autoRefreshToken will hang indefinitely waiting for a response.
      try {
        const raw = safeGetItem('odv-supabase-auth');
        if (raw) {
          const parsed = JSON.parse(raw);
          const expiresAt = parsed?.expires_at; // unix seconds
          if (expiresAt) {
            const expiredAgo = Date.now() / 1000 - expiresAt;
            if (expiredAgo > 3600) { // expired more than 1 hour ago
              console.warn(`[AuthContext] Stale session detected (expired ${Math.round(expiredAgo / 60)}min ago) — clearing to prevent hang`);
              safeRemoveItem('odv-supabase-auth');
              setUser(null);
              setLoading(false);
              authCompleted = true;
              return;
            }
          }
        }
      } catch {
        // localStorage parse error — ignore and proceed normally
      }

      // getSession with timeout — prevents hang when token refresh is slow
      try {
        const { data: { session }, error } = await Promise.race([
          auth.getSession(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('getSession timed out')), 5000))
        ]);

        if (error) {
          log.error?.('Session check error', error);
        }

        log.debug?.('Initial session check result', { hasSession: !!session });
        setUser(session?.user ?? null);

        if (session?.user) {
          try {
            await syncSession(session);
            log.debug?.('Session synced to data client (existing session)');
          } catch (syncError) {
            log.error?.('Failed to sync session', syncError);
          }
        }
      } catch (_error) {
        log.error?.('Session check failed (may have timed out)', _error.message);
        // CRITICAL: If onAuthStateChange already established the session, don't destroy it!
        // getSession() can timeout while the session is actually valid (slow token refresh).
        // Signing out here would kill the working session and hang all downstream queries.
        if (authCompleted) {
          log.debug?.('getSession timed out but session already established via onAuthStateChange — keeping session');
        } else {
          // No session was established by any path — sign out to reset client state
          try {
            await auth.signOut({ scope: 'local' });
            log.debug?.('Signed out locally after getSession timeout — user will need to re-login');
          } catch {
            // If even signOut hangs, clear localStorage directly
            localStorage.removeItem('odv-supabase-auth');
          }
          setUser(null);
        }
      } finally {
        log.debug?.('AuthContext: bootstrapAuth complete, setting loading=false');
        setLoading(false);
        authCompleted = true;
      }
    };

    bootstrapAuth();

    // Track initialization and role resolution state.
    // During OAuth PKCE, onAuthStateChange fires 3 times:
    //   1. SIGNED_IN (inside _initialize → _exchangeCodeForSession — client NOT ready)
    //   2. INITIAL_SESSION (_initialize complete — client IS ready)
    //   3. SIGNED_IN again (redundant)
    // We must NOT run badge queries on event #1 — they hang because the client
    // is mid-initialization. We wait for INITIAL_SESSION to signal readiness.
    let clientInitialized = false;
    let rolesResolved = false;
    let activeRoleUserId = null;

    // Listen for auth changes
    const { data: { subscription } } = auth.onAuthStateChange(
      async (event, session) => {
        log.debug?.('Auth state change', { event, email: session?.user?.email });
        const currentUser = session?.user;
        setUser(currentUser ?? null);

        // Track client initialization. INITIAL_SESSION means _initialize is done
        // and the client is fully ready for REST queries.
        if (event === 'INITIAL_SESSION') {
          clientInitialized = true;
        }

        // Mark auth as ready when session is established
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
          console.log('[AuthContext] ✅ Session established, setting authReady=true', { event });
          setAuthReady(true);
          authCompleted = true;

          // Clear OAuth code from URL after successful exchange
          if (window.location.search.includes('code=')) {
            console.log('[AuthContext] Clearing OAuth code from URL after successful exchange');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }

        // Sync session to data client for authenticated queries
        if (currentUser) {
          try {
            await syncSession(session);
          } catch (syncError) {
            log.error?.('Failed to sync session on auth change', syncError);
          }
        }

        if (currentUser) {
          // Skip if roles already resolved (prevents redundant fetches from multiple events)
          if (rolesResolved) {
            log.debug?.('Roles already resolved, skipping', { event });
            setLoading(false);
            return;
          }

          // Skip SIGNED_IN that fires BEFORE INITIAL_SESSION — client is mid-init,
          // queries will hang. INITIAL_SESSION will fire next with a ready client.
          if (!clientInitialized && event === 'SIGNED_IN') {
            console.log('[AuthContext] Skipping role resolution — client still initializing (pre-INITIAL_SESSION)');
            return;
          }

          setRolesReady(false);
          activeRoleUserId = currentUser.id;

          // Role resolution with timeout safety net (6s).
          // Should resolve instantly now that we skip the pre-init event,
          // but timeout remains as a guardrail for slow connections.
          const resolveRoles = async () => {
            const userId = currentUser.id;
            let resolvedRole = 'valet';
            let resolvedBadges = [];

            const client = supabase;
            setUserData(null);

            // Client portal users — check user_metadata.role before badge resolution
            const metaRole = currentUser.user_metadata?.role;
            if (metaRole === 'client') {
              const clientId = currentUser.user_metadata?.client_id;
              log.debug?.('Client portal user detected', { clientId, email: currentUser.email });
              setUserData({ clientId, isClient: true, companyName: currentUser.user_metadata?.company_name });
              return { resolvedBadges: ['client'], resolvedRole: 'client' };
            }

            // Never query valets without a verified session for THIS user: a missing
            // or stale token sends the anon key (anon-valets review 2026-09-30). This
            // throws, so the catch below keeps rolesResolved=false and the next auth
            // event (TOKEN_REFRESHED / SIGNED_IN) or the backoff retry runs it again.
            await requireVerifiedSession(auth, userId);

            const { data: valetDoc, error: valetError } = await client
              .from('valets')
              .select(VALET_PROFILE_COLUMNS)
              .or(`auth_user_id.eq.${userId},user_id.eq.${userId}`)
              .limit(1)
              .maybeSingle();

            if (valetError && valetError.code !== 'PGRST116') {
              log.error?.('Error fetching valet data', valetError);
            }
            setValetProfile(valetDoc || null);

            // Sync OAuth data (Discord ID, avatar) to valet profile — fire-and-forget
            if (valetDoc?.id && currentUser) {
              syncOAuthDataToProfile(currentUser, valetDoc).catch(err =>
                log.warn?.('OAuth data sync failed (non-fatal)', err.message)
              );
            }

            if (valetDoc?.id) {
              const { data: badgeData, error: badgeError } = await client
                .from('badge_assignments')
                .select('badge_type, is_active')
                .eq('valet_id', valetDoc.id)
                .eq('is_active', true);

              if (badgeError) {
                log.error?.('Error fetching badges', badgeError);
                resolvedBadges = [];
              } else {
                resolvedBadges = (badgeData || []).map(b => b.badge_type).filter(Boolean);
                log.debug?.('Loaded user badges', { valetId: valetDoc.id, badges: resolvedBadges });
                resolvedRole = deriveRoleFromBadges(resolvedBadges, resolvedRole || 'valet');
              }
            } else {
              const { data: badgeData, error: badgeError } = await client
                .from('badge_assignments')
                .select('badge_type, is_active')
                .eq('user_id', userId)
                .eq('is_active', true);

              if (badgeError) {
                log.error?.('Error fetching badges (user scope)', badgeError);
                resolvedBadges = [];
              } else if (badgeData?.length > 0) {
                resolvedBadges = badgeData.map(b => b.badge_type).filter(Boolean);
                resolvedRole = deriveRoleFromBadges(resolvedBadges, resolvedRole || 'user');
                log.debug?.('Determined user role from badges (no valet profile)', { role: resolvedRole });
              } else {
                resolvedBadges = [];
                log.debug?.('No badges found, defaulting to fallback role');
              }
            }

            // Location-scoped leadership check (canonical: badge OR captain-roster OR account_type).
            // Grants dashboard access to captains/kots who live on location_rosters rather than a
            // global badge (Codex scope fix 2026-07-08). Non-fatal — badges alone still gate access.
            let resolvedRosterLeader = false;
            try {
              const { data: leaderFlag, error: leaderError } = await client
                .rpc('is_kot_leader', { check_user_id: userId });
              if (leaderError) {
                log.warn?.('is_kot_leader check failed (non-fatal)', leaderError.message);
              } else {
                resolvedRosterLeader = leaderFlag === true;
              }
            } catch (leaderErr) {
              log.warn?.('is_kot_leader threw (non-fatal)', leaderErr?.message);
            }

            return { resolvedBadges, resolvedRole, resolvedRosterLeader };
          };

          const applyResolvedRoles = ({ resolvedBadges, resolvedRole, resolvedRosterLeader = false }, source = 'initial') => {
            if (!authEffectActive) return;
            if (activeRoleUserId !== currentUser.id) {
              log.debug?.('Skipping stale role resolution result', { source, userId: currentUser.id });
              return;
            }
            setUserBadges(resolvedBadges);
            setIsRosterLeader(resolvedRosterLeader);
            setUserRole(resolvedRole || 'valet');
            setRolesDegraded(false); // real resolution landed — clear any degraded flag
            rolesResolved = true; // Mark as done so subsequent events skip
            setRolesReady(true);
            log.debug?.('Role resolution applied', { source, role: resolvedRole, badgeCount: resolvedBadges?.length || 0 });
          };

          const roleResolution = resolveRoles();

          try {
            const roleResult = await Promise.race([
              roleResolution,
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Role resolution timed out after 6s')), 6000)
              ),
            ]);
            applyResolvedRoles(roleResult);
          } catch (_error) {
            console.warn('[AuthContext] Role resolution failed or timed out:', _error.message);
            log.error?.('Error fetching user data', _error);
            // Provisional default so the app renders — but flag it as degraded (NOT a real
            // read) so guards don't treat a founder as badge-less during an incident.
            if (authEffectActive && activeRoleUserId === currentUser.id) {
              setRolesDegraded(true);
            }
            setUserRole((currentRole) => currentRole === null ? 'valet' : currentRole);
            setUserBadges((currentBadges) => currentBadges.length > 0 ? currentBadges : []);
            // Don't mark rolesResolved — let the next auth event retry too. In addition,
            // kick a FRESH resolveRoles() attempt with backoff (the original promise may be
            // wedged on the same stalled connection, so awaiting it can never resolve).
            const retryAfterBackoff = async () => {
              await new Promise((r) => setTimeout(r, 1500));
              if (!authEffectActive || activeRoleUserId !== currentUser.id) return;
              try {
                const retryResult = await Promise.race([
                  resolveRoles(),
                  new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Role resolution retry timed out after 6s')), 6000)
                  ),
                ]);
                applyResolvedRoles(retryResult, 'retry-after-timeout'); // clears rolesDegraded on success
              } catch (retryError) {
                log.error?.('Role resolution retry failed — staying degraded', retryError);
                // Leave rolesDegraded=true; the next auth event (TOKEN_REFRESHED on heal) retries.
              }
            };
            retryAfterBackoff();
            setRolesReady(true);
          } finally {
            if (authEffectActive) {
              setRolesReady(true);
            }
          }
        } else {
          // User logged out - clear all user data
          log.debug?.('User logged out, clearing auth context state');
          activeRoleUserId = null;
          setUserData(null);
          setValetProfile(null);
          setUserRole(null);
          setUserBadges([]);
          setIsRosterLeader(false);
          setRolesReady(false);
          setRolesDegraded(false);
        }
        setLoading(false);
      }
    );

    // Visibility change listener: refresh session when user tabs back
    // Mobile browsers often let sessions expire while backgrounded
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const { data: { session } } = await auth.getSession();
        if (!session) return;
        const expiresAt = session.expires_at * 1000;
        const fiveMinutes = 5 * 60 * 1000;
        if (expiresAt - Date.now() < fiveMinutes) {
          log.debug?.('Session expiring soon after tab focus, refreshing');
          await Promise.race([
            auth.refreshSession(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('refresh timeout')), 5000))
          ]);
        }
      } catch (err) {
        log.warn?.('Visibility-change session refresh failed', err.message);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      authEffectActive = false;
      clearTimeout(emergencyTimeout);
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const login = (email, password) => auth.signInWithPassword({ email, password });

  const logout = async () => {
    log.debug?.('AuthContext: Starting logout');
    let signOutError = null;

    // With unified client, auth and supabase.auth are the same instance
    // Only need to call signOut once
    try {
      log.debug?.('AuthContext: Calling auth.signOut');
      const { error } = await auth.signOut();
      if (error) {
        // AuthSessionMissingError is expected if session already expired - not a real error
        if (error.name === 'AuthSessionMissingError' || error.message?.includes('session missing')) {
          log.debug?.('AuthContext: Session already expired, proceeding with cleanup');
        } else {
          signOutError = error;
          log.error?.('AuthContext: signOut error', error);
        }
      } else {
        log.debug?.('AuthContext: signOut successful');
      }
    } catch (_error) {
      // Handle thrown errors the same way
      if (_error.name === 'AuthSessionMissingError' || _error.message?.includes('session missing')) {
        log.debug?.('AuthContext: Session already expired, proceeding with cleanup');
      } else {
        signOutError = _error;
        log.error?.('AuthContext: signOut exception', _error);
      }
    }

    // 1. Clear localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('auth') || key.includes('supabase') || key.includes('sb-'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      log.debug?.('Removing localStorage key', { key });
      localStorage.removeItem(key);
    });

    // 2. Clear sessionStorage
    sessionStorage.clear();
    log.debug?.('Cleared sessionStorage');

    // 3. Clear ALL cookies (including Supabase session cookies)
    document.cookie.split(";").forEach((c) => {
      const cookieName = c.split("=")[0].trim();
      document.cookie = `${cookieName}=;expires=${new Date(0).toUTCString()};path=/`;
      console.log(`🗑️ Cleared cookie: ${cookieName}`);
    });

    // 4. Clear IndexedDB (Supabase may store session data here)
    try {
      const dbs = await indexedDB.databases?.() || [];
      for (const db of dbs) {
        log.debug?.('Deleting IndexedDB database', { name: db.name });
        await new Promise((resolve, reject) => {
          const req = indexedDB.deleteDatabase(db.name);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        });
      }
    } catch (indexedDbError) {
      log.warn?.('Could not clear IndexedDB', indexedDbError);
    }

    log.debug?.('Cleared all storage mechanisms');

    // 5. Clear local state
    setUser(null);
    setUserData(null);
    setValetProfile(null);
    setUserRole(null);
    setUserBadges([]);
    setIsRosterLeader(false);
    setRolesDegraded(false);
    log.debug?.('Cleared all auth state');

    log.debug?.('AuthContext: Logout complete');
    return {
      success: !signOutError,
      error: signOutError || undefined
    };
  };

  // Helper: check if user has dashboard access (non-valet routes).
  // Either a global leadership badge OR active location-scoped leadership (is_kot_leader).
  const hasDashboardAccess = isRosterLeader || userBadges.some(badge => DASHBOARD_ACCESS_BADGES.includes(badge));

  // Helper: check if user has a specific badge
  const hasBadge = (badgeType) => userBadges.includes(badgeType);

  const value = {
    user,
    userData,
    valetProfile,
    userRole,
    userBadges,
    loading,
    authReady, // true when OAuth processing is complete and client is unblocked
    rolesReady,
    rolesDegraded, // true when role resolution failed/timed out — reads are provisional, not truth

    login,
    logout,
    hasBadge,
    hasDashboardAccess,
    isRosterLeader,
    isAuthenticated: !!user,
    // Role checks based on badge hierarchy
    isValet: userRole === 'valet',
    isSupervisor: userRole === 'supervisor',
    isManager: userRole === 'manager',
    isAdmin: userRole === 'admin',
    isSuperAdmin: userRole === 'super-admin',
    isOwner: userRole === 'owner',
    // Founder badge — the ONLY grantor of authority badges (mirrors the DB
    // RLS gate actor_holds_founder_badge() in 20260612150000). Granting an
    // access/admin-category badge requires this; UI hides those controls otherwise.
    isFounder: userBadges.includes('founder'),
    // Legacy compatibility
    isKOT: userRole === 'manager' || userBadges.includes('kot'),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
